<template>
  <div class="tool-approval-request">
    <br />
    <span>The following {{ pluralize(toolCount, 'tool') }} will be called:</span>

    <slot></slot>

    <div class="approvement-actions">
      <span v-if="isApprovedOverall">Approved</span>
      <span v-else-if="isRejectedOverall">Rejected</span>
      <span v-else>Approve?</span>
      <button
        :disabled="isApprovedOverall"
        :readonly="hasDecision"
        type="button"
        class="btn btn--reject"
        title="Reject"
        aria-label="Reject"
        @click="handleReject"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span class="btn-icon" v-html="faTimes" />
      </button>
      <button
        :disabled="isRejectedOverall"
        :readonly="hasDecision"
        type="button"
        class="btn btn--approve"
        title="Approve"
        aria-label="Approve"
        @click="handleApprove"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span class="btn-icon" v-html="faCheck" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { pluralize } from '@/plugins/filters';
import { computed, defineProps, inject, ref } from 'vue';
import faTimes from '@fortawesome/fontawesome-pro/svgs/light/times.svg?raw';
import faCheck from '@fortawesome/fontawesome-pro/svgs/light/check.svg?raw';

const { toolRequestId, toolCount, isApproved, isRejected } = defineProps({
  toolRequestId: {
    type: String,
    required: true,
  },
  toolCount: {
    type: Number,
    required: true,
  },
  isApproved: {
    type: Boolean,
    required: false,
    default: false,
  },
  isRejected: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const deepChatRef = inject('deepChatRef');
const isApprovedLocal = ref(false);
const isRejectedLocal = ref(false);
const isApprovedOverall = computed(() => isApproved || isApprovedLocal.value);
const isRejectedOverall = computed(() => isRejected || isRejectedLocal.value);
const hasDecision = computed(() => isApprovedOverall.value || isRejectedOverall.value);

function findMessageByToolRequestId(toolRequestId) {
  return deepChatRef.value
    .getMessages()
    .find(m =>
      m.custom?.contents?.some(
        c => c.$type === 'tool-approval-request-html' && c.toolRequestId === toolRequestId
      )
    );
}

async function handleApprove() {
  await handleAction(true);
}

async function handleReject() {
  await handleAction(false);
}

async function handleAction(isApproved) {
  if (hasDecision.value) {
    return;
  }

  const messageRequest = findMessageByToolRequestId(toolRequestId);

  const toolCallApprovals = messageRequest.custom.contents
    .filter(c => c.toolCall)
    .map(c => ({
      $type: 'toolApprovalResponse',
      requestId: c.requestId,
      approved: isApproved,
      toolCall: c.toolCall,
    }));

  const messageResponse = {
    text: isApproved ? 'Approve' : 'Reject',
    custom: {
      contents: toolCallApprovals,
    },
  };

  deepChatRef.value.submitUserMessage(messageResponse);
  isApprovedLocal.value = isApproved;
  isRejectedLocal.value = !isApproved;
}
</script>

<style>
:host {
  display: block;
}

.tool-approval-request {
  display: block;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.approvement-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-style: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 160ms ease-out;

  &:focus-visible {
    outline: 2px solid var(--chatbot-focus-ring-color);
    outline-offset: 2px;
  }
}

.btn:disabled {
  opacity: 0.1;
}

.btn[readonly] {
  pointer-events: none;
}

.btn--approve {
  color: var(--chatbot-approve-color);
  background: var(--chatbot-approve-background);

  &:hover,
  &:focus-visible {
    background: var(--chatbot-approve-hover-background);
  }

  &:active {
    background: var(--chatbot-approve-active-background);
  }
}

.btn--reject {
  color: var(--chatbot-reject-color);
  background: var(--chatbot-reject-background);

  &:hover,
  &:focus-visible {
    background: var(--chatbot-reject-hover-background);
  }

  &:active {
    background: var(--chatbot-reject-active-background);
  }
}

.btn-icon {
  display: inline-flex;
}

.btn-icon svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
}
</style>
